// The Pick Queue's filter controls, shared with the "Which wave?" page
// (/picking/choose) so the two screens render — and behave — identically
// above their cards:
//
//   • usePickFilters   — the filter state + its localStorage persistence
//                        (same keys on both pages, so going back shows the
//                        same selection) + the 13:00 cut-off re-check that
//                        governs the clock toggle.
//   • TierToggle       — the Low/High radiogroup in the title bar.
//   • SelectorFilterRows — delivery segments (+ clock toggle after the
//                        cut-off), age segments (+ the Custom date range
//                        popover) and the sales-channel select, with the
//                        counts the selector response carries for each.
//
// Markup, classes and order are exactly the queue's; nothing here is
// styled on its own.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownToLine, ArrowUpToLine, CalendarRange, Clock, Truck, Zap } from "lucide-react";
import { useTranslation } from "../../i18n";
import { useOrderCutoff } from "../helpers/useOrderCutoff";
import {
    readStoredAgeFilter, readStoredChannelFilter, readStoredDate, readStoredDeliveryFilter, readStoredDueFilter,
    readStoredTier,
    AGE_FILTER_STORAGE_KEY, AGE_FROM_STORAGE_KEY, AGE_TO_STORAGE_KEY, CHANNEL_FILTER_STORAGE_KEY,
    DELIVERY_FILTER_STORAGE_KEY, DUE_FILTER_STORAGE_KEY, TIER_STORAGE_KEY,
    type AgeFilter, type DeliveryFilter, type PickFilters, type Tier,
} from "./selectorShared";

// How often the selector re-checks the London clock so the clock toggle
// appears at 13:00 (and drops away after midnight) without a reload.

// Ordered urgency-first: oldest at the start so pickers scanning the
// row from left to right see "what's been waiting longest" first.
export const AGE_SEGMENTS: AgeFilter[] = ["all", "2+", "1-2", "<1", "custom"];
export const DELIVERY_SEGMENTS: DeliveryFilter[] = ["all", "express", "standard"];

export interface AgeCounts {
    all: number;
    "<1": number;
    "1-2": number;
    "2+": number;
    custom: number;
}

export interface DeliveryCounts {
    all: number;
    express: number;
    standard: number;
}

export interface ChannelCount {
    items: number;
    label: string;
}

export const EMPTY_AGE_COUNTS: AgeCounts = { all: 0, "<1": 0, "1-2": 0, "2+": 0, custom: 0 };
export const EMPTY_DELIVERY_COUNTS: DeliveryCounts = { all: 0, express: 0, standard: 0 };

export interface PickFiltersState {
    /** The effective filter set (dueFilter = clock toggle on AND past the cut-off). */
    filters: PickFilters;
    tier: Tier;
    ageFilter: AgeFilter;
    ageFrom: string;
    ageTo: string;
    deliveryFilter: DeliveryFilter;
    channelFilter: string;
    /** The picker's stored clock-toggle choice (only effective after the cut-off). */
    dueOn: boolean;
    afterCutoff: boolean;
    customOpen: boolean;
    setCustomOpen: (next: boolean | ((o: boolean) => boolean)) => void;
    setTier: (next: Tier) => void;
    setAgeFilter: (next: AgeFilter) => void;
    setAgeFrom: (next: string) => void;
    setAgeTo: (next: string) => void;
    setDeliveryFilter: (next: DeliveryFilter) => void;
    setChannelFilter: (next: string) => void;
    setDueOn: (next: boolean) => void;
}

// `initial` seeds the state (the chooser page is handed the queue's live
// set); anything not given comes from the same localStorage the queue
// reads. Every setter persists, so both pages stay in step.
export const usePickFilters = (initial?: Partial<PickFilters>): PickFiltersState => {
    const [ageFilter, setAgeFilterState] = useState<AgeFilter>(() => initial?.ageFilter ?? readStoredAgeFilter());
    const [ageFrom, setAgeFromState] = useState<string>(() => initial?.ageFrom ?? readStoredDate(AGE_FROM_STORAGE_KEY));
    const [ageTo, setAgeToState] = useState<string>(() => initial?.ageTo ?? readStoredDate(AGE_TO_STORAGE_KEY));
    const [customOpen, setCustomOpen] = useState(false);
    const [deliveryFilter, setDeliveryFilterState] = useState<DeliveryFilter>(() => initial?.deliveryFilter ?? readStoredDeliveryFilter());
    const [channelFilter, setChannelFilterState] = useState<string>(() => initial?.channelFilter ?? readStoredChannelFilter());
    // Clock toggle. `dueOn` is the picker's stored choice; it only takes
    // effect while the toggle is visible (after the 13:00 London
    // cut-off), so a choice left on overnight can't silently narrow the
    // next morning's queue before the toggle is even on screen.
    const [dueOn, setDueOnState] = useState<boolean>(() => initial?.dueFilter ?? readStoredDueFilter());
    // Past today's order cut-off for this warehouse (Settings › Dispatch ›
    // Channel cut-offs): re-checked every minute so the toggle shows up at
    // the cut-off on a screen that has been sitting open since the morning.
    const { afterCutoff } = useOrderCutoff();
    const dueFilter = afterCutoff && dueOn;
    const [tier, setTierState] = useState<Tier>(() => initial?.tier ?? readStoredTier());

    const setAgeFilter = useCallback((next: AgeFilter) => {
        setAgeFilterState(next);
        try { window.localStorage.setItem(AGE_FILTER_STORAGE_KEY, next); } catch { /* ignore */ }
        if (next === "custom") setCustomOpen(true);
    }, []);

    const setAgeFrom = useCallback((next: string) => {
        setAgeFromState(next);
        try {
            if (next) window.localStorage.setItem(AGE_FROM_STORAGE_KEY, next);
            else window.localStorage.removeItem(AGE_FROM_STORAGE_KEY);
        } catch { /* ignore */ }
    }, []);

    const setAgeTo = useCallback((next: string) => {
        setAgeToState(next);
        try {
            if (next) window.localStorage.setItem(AGE_TO_STORAGE_KEY, next);
            else window.localStorage.removeItem(AGE_TO_STORAGE_KEY);
        } catch { /* ignore */ }
    }, []);

    const setDeliveryFilter = useCallback((next: DeliveryFilter) => {
        setDeliveryFilterState(next);
        try { window.localStorage.setItem(DELIVERY_FILTER_STORAGE_KEY, next); } catch { /* ignore */ }
    }, []);

    const setChannelFilter = useCallback((next: string) => {
        setChannelFilterState(next);
        try { window.localStorage.setItem(CHANNEL_FILTER_STORAGE_KEY, next); } catch { /* ignore */ }
    }, []);

    const setTier = useCallback((next: Tier) => {
        setTierState(next);
        try { window.localStorage.setItem(TIER_STORAGE_KEY, next); } catch { /* ignore */ }
    }, []);

    const setDueOn = useCallback((next: boolean) => {
        setDueOnState(next);
        try { window.localStorage.setItem(DUE_FILTER_STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
    }, []);


    const filters: PickFilters = useMemo(
        () => ({ tier, ageFilter, deliveryFilter, channelFilter, ageFrom, ageTo, dueFilter }),
        [tier, ageFilter, deliveryFilter, channelFilter, ageFrom, ageTo, dueFilter],
    );

    return {
        filters, tier, ageFilter, ageFrom, ageTo, deliveryFilter, channelFilter, dueOn, afterCutoff,
        customOpen, setCustomOpen,
        setTier, setAgeFilter, setAgeFrom, setAgeTo, setDeliveryFilter, setChannelFilter, setDueOn,
    };
};

export const TierToggle = ({ tier, onChange, busy }: { tier: Tier; onChange: (next: Tier) => void; busy: boolean }) => {
    const { t } = useTranslation();
    return (
        <div className="picking-tier-toggle" role="radiogroup" aria-label={t("picking_tier")}>
            <button
                type="button"
                role="radio"
                aria-checked={tier === "low"}
                className={`picking-tier-pill${tier === "low" ? " picking-tier-pill--active" : ""}`}
                onClick={() => onChange("low")}
                disabled={busy}
                title={t("picking_tier_low")}
                aria-label={t("picking_tier_low")}
            >
                <ArrowDownToLine size={16} strokeWidth={2.2} aria-hidden="true" />
            </button>
            <button
                type="button"
                role="radio"
                aria-checked={tier === "high"}
                className={`picking-tier-pill${tier === "high" ? " picking-tier-pill--active" : ""}`}
                onClick={() => onChange("high")}
                disabled={busy}
                title={t("picking_tier_high")}
                aria-label={t("picking_tier_high")}
            >
                <ArrowUpToLine size={16} strokeWidth={2.2} aria-hidden="true" />
            </button>
        </div>
    );
};

export interface SelectorFilterCounts {
    ageCounts: AgeCounts;
    deliveryCounts: DeliveryCounts;
    channelCounts: Record<string, ChannelCount>;
    dueCount: number;
}

export const SelectorFilterRows = ({ f, counts, busy }: { f: PickFiltersState; counts: SelectorFilterCounts; busy: boolean }) => {
    const { t } = useTranslation();
    const { ageCounts, deliveryCounts, channelCounts, dueCount } = counts;
    const dueFilter = f.filters.dueFilter;

    const channelOptions = useMemo(
        () => Object.entries(channelCounts).sort(([codeA, a], [codeB, b]) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" }) || codeA.localeCompare(codeB)),
        [channelCounts],
    );

    // Close the Custom date popover when the user clicks outside of
    // its wrapper. The Custom segment button itself sits inside the
    // wrapper so clicking it toggles state via the segment's onClick.
    const customRef = useRef<HTMLDivElement | null>(null);
    const { customOpen, setCustomOpen } = f;
    useEffect(() => {
        if (!customOpen) return;
        const handler = (e: MouseEvent | TouchEvent) => {
            if (!customRef.current) return;
            if (customRef.current.contains(e.target as Node)) return;
            setCustomOpen(false);
        };
        window.addEventListener("mousedown", handler);
        window.addEventListener("touchstart", handler);
        return () => {
            window.removeEventListener("mousedown", handler);
            window.removeEventListener("touchstart", handler);
        };
    }, [customOpen, setCustomOpen]);

    const ageSegmentLabel = (seg: AgeFilter): string => {
        switch (seg) {
            case "all":    return t("picking_filter_all");
            case "<1":     return t("picking_filter_lt1");
            case "1-2":    return t("picking_filter_1to2");
            case "2+":     return t("picking_filter_2_plus");
            case "custom": return t("picking_filter_custom");
        }
    };

    const deliverySegmentLabel = (seg: DeliveryFilter): string => {
        switch (seg) {
            case "all":      return t("picking_filter_all");
            case "express":  return t("picking_filter_express_label");
            case "standard": return t("picking_filter_standard_label");
        }
    };

    return (
        <>
            <div className="picking-filter-row">
                <div className="picking-filter picking-filter--delivery" role="tablist" aria-label={t("picking_filter_delivery_aria")}>
                    {DELIVERY_SEGMENTS.map(seg => {
                        const count = deliveryCounts[seg] ?? 0;
                        const active = f.deliveryFilter === seg;
                        const icon = seg === "express"  ? <Zap   size={16} fill="currentColor" stroke="none" aria-hidden="true" />
                                  : seg === "standard" ? <Truck size={16} strokeWidth={2} aria-hidden="true" />
                                  : null;
                        const label = deliverySegmentLabel(seg);
                        return (
                            <button
                                key={seg}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                className={`picking-filter-btn${active ? " picking-filter-btn--on" : ""}${icon ? " picking-filter-btn--icon" : ""}`}
                                onClick={() => f.setDeliveryFilter(seg)}
                                disabled={busy}
                                title={label}
                                aria-label={label}
                            >
                                <span className="picking-filter-label">{icon ?? label}</span>
                                <span className={`picking-filter-count${count === 0 ? " picking-filter-count--zero" : ""}`}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* "Must ship today" clock toggle — only after the 13:00
                    London dispatch cut-off, when what's left in the queue
                    splits into today's dispatch and tomorrow's. An on/off
                    toggle (not a segment): it stacks with the delivery,
                    age and channel filters. Badge = items due today or
                    overdue on this tier, unfiltered like the other badges. */}
                {f.afterCutoff && (
                    <div className="picking-filter picking-filter--due">
                        <button
                            type="button"
                            aria-pressed={dueFilter}
                            className={`picking-filter-btn picking-filter-btn--icon picking-filter-btn--due${dueFilter ? " picking-filter-btn--on" : ""}`}
                            onClick={() => f.setDueOn(!f.dueOn)}
                            disabled={busy}
                            title={t("picking_filter_due_today")}
                            aria-label={t("picking_filter_due_today")}
                        >
                            <span className="picking-filter-label">
                                <Clock size={16} strokeWidth={2.2} aria-hidden="true" />
                            </span>
                            <span className={`picking-filter-count${dueCount === 0 ? " picking-filter-count--zero" : ""}`}>
                                {dueCount}
                            </span>
                        </button>
                    </div>
                )}
            </div>

            <div className="picking-filter picking-filter--age" role="tablist" aria-label={t("picking_filter_age_aria")}>
                {AGE_SEGMENTS.map(seg => {
                    const count = ageCounts[seg] ?? 0;
                    const active = f.ageFilter === seg;
                    const label = ageSegmentLabel(seg);
                    if (seg === "custom") {
                        return (
                            <div
                                key={seg}
                                className="picking-filter-custom-wrapper"
                                ref={customRef}
                            >
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    className={`picking-filter-btn picking-filter-btn--icon picking-filter-btn--custom${active ? " picking-filter-btn--on" : ""}`}
                                    onClick={() => {
                                        if (active) setCustomOpen(o => !o);
                                        else f.setAgeFilter("custom");
                                    }}
                                    disabled={busy}
                                    title={label}
                                    aria-label={label}
                                    aria-expanded={customOpen}
                                >
                                    <span className="picking-filter-label">
                                        <CalendarRange size={16} strokeWidth={2} aria-hidden="true" />
                                    </span>
                                    <span className={`picking-filter-count${count === 0 ? " picking-filter-count--zero" : ""}`}>
                                        {count}
                                    </span>
                                </button>
                                {customOpen && (
                                    <div className="picking-custom-popover" role="dialog" aria-label={t("picking_filter_custom")}>
                                        <label className="picking-custom-field">
                                            <span className="picking-custom-field-label">{t("picking_filter_custom_from")}</span>
                                            <input
                                                type="date"
                                                value={f.ageFrom}
                                                onChange={e => f.setAgeFrom(e.target.value)}
                                                disabled={busy}
                                                className="picking-custom-input"
                                            />
                                        </label>
                                        <label className="picking-custom-field">
                                            <span className="picking-custom-field-label">{t("picking_filter_custom_to")}</span>
                                            <input
                                                type="date"
                                                value={f.ageTo}
                                                onChange={e => f.setAgeTo(e.target.value)}
                                                disabled={busy}
                                                className="picking-custom-input"
                                            />
                                        </label>
                                        <div className="picking-custom-actions">
                                            <button
                                                type="button"
                                                className="picking-custom-clear"
                                                onClick={() => { f.setAgeFrom(""); f.setAgeTo(""); }}
                                                disabled={busy || (!f.ageFrom && !f.ageTo)}
                                            >
                                                {t("picking_filter_custom_clear")}
                                            </button>
                                            <button
                                                type="button"
                                                className="picking-custom-done"
                                                onClick={() => setCustomOpen(false)}
                                                disabled={busy}
                                            >
                                                {t("picking_filter_custom_done")}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }
                    return (
                        <button
                            key={seg}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            className={`picking-filter-btn${active ? " picking-filter-btn--on" : ""}`}
                            onClick={() => f.setAgeFilter(seg)}
                            disabled={busy}
                            title={label}
                            aria-label={label}
                        >
                            <span className="picking-filter-label">{label}</span>
                            <span className={`picking-filter-count${count === 0 ? " picking-filter-count--zero" : ""}`}>
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            {channelOptions.length > 1 && (
                <label className="picking-channel-filter">
                    <span className="picking-channel-filter-label">{t("picking_filter_channel_label")}</span>
                    <select
                        className="picking-channel-filter-select"
                        value={f.channelFilter}
                        onChange={event => f.setChannelFilter(event.target.value)}
                        disabled={busy}
                    >
                        <option value="all">{t("picking_filter_channel_all")}</option>
                        {channelOptions.map(([code, channel]) => (
                            <option key={code} value={code}>
                                {channel.label} ({channel.items})
                            </option>
                        ))}
                    </select>
                </label>
            )}
        </>
    );
};
